'use client';

import {
  Box,
  BoxProps,
  Flex,
  Grid,
  GridItem,
  Link,
  List,
  ListItem,
  Modal,
  ModalBody,
  Typography
} from '@coolblue-development/freddy';
import readExplanationTrigger from '@coolblue-development/ga4-events/triggers/CoolbluesChoice/readExplanationTrigger';
import selectAllTrigger from '@coolblue-development/ga4-events/triggers/CoolbluesChoice/selectAllTrigger';
import { ArrowRight } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { Image } from '@coolblue-development/next-image/client';
import { Product } from '@features/product/typings';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import { ReactElement, useState } from 'react';

type Props = {
  productData: Product;
  label: ReactElement;
  showOnMobile?: boolean;
  coolbluesChoiceLink: string;
  coolbluesChoiceArticleLink?: string;
};

const CbcLabelContainer = ({ label, ...boxProps }: { label: ReactElement } & BoxProps) => {
  return <Box {...boxProps}>{label}</Box>;
};

export default function CoolbluesChoiceBanner({
  productData,
  label,
  showOnMobile = false,
  coolbluesChoiceLink,
  coolbluesChoiceArticleLink
}: Props): ReactElement | undefined {
  const { Trans, t } = useTranslation('webshop.productPage.coolbluesChoice');
  const modalTranslations = useFreddyTranslations('modal');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const { isMobile } = useBreakpoints();
  const { trackEvent } = useAnalytics();

  if (!productData.coolbluesChoice?.content?.title) return;

  if ((!showOnMobile && isMobile) || (showOnMobile && !isMobile)) {
    return <></>;
  }

  const { title, specialist, explanationBullets } = productData.coolbluesChoice.content;
  const hasModal = !!explanationBullets;
  const { pluralName } = productData.productType;

  const modalMobilePadding = showOnMobile ? 300 : 500;

  const handleReadMoreClick = () => {
    setModalIsOpen(true);
    trackEvent(
      readExplanationTrigger({
        subject_id: t('ourChoice', { title })
      })
    );
  };

  const handleSeeAllChoicesClick = () => {
    trackEvent(
      selectAllTrigger({
        index: 1,
        total_indices: 1,
        url: coolbluesChoiceLink,
        redirect: t('seeAllChoices', { productType: pluralName })
      })
    );
  };

  return (
    <div>
      {isMobile && (
        <Typography fontWeight='bold' mb={200} fontSize='lg'>
          {t('title')}
        </Typography>
      )}
      <Flex gap={500} alignItems='center'>
        <CbcLabelContainer label={label} flexGrow={showOnMobile ? 1 : 0} />
        <Box>
          <Typography
            variant={showOnMobile ? 'body' : 'h4'}
            component='p'
            mb={100}
            mr={showOnMobile ? 200 : 0}
            lineHeight={400}
          >
            <Trans t={t} shouldUnescape i18nKey='ourChoice' values={{ title }} />
          </Typography>

          {hasModal && (
            <Link
              component='button'
              type='button'
              onClick={() => handleReadMoreClick()}
              startIcon={<ArrowRight />}
            >
              {t('seeMore')}
            </Link>
          )}
        </Box>

        {hasModal && (
          <Modal
            placement={{ xs: 'bottom', md: 'center' }}
            open={modalIsOpen}
            onClose={() => setModalIsOpen(false)}
            size='large'
            title={t('ourChoice', { title })}
            translations={modalTranslations}
            body={
              <ModalBody noPadding>
                <Grid>
                  <GridItem
                    column={{ xs: 'span 12', md: 'span 8' }}
                    pl={modalMobilePadding}
                    pr={specialist ? (showOnMobile ? 300 : 0) : 500}
                  >
                    {!!explanationBullets && (
                      <List>
                        {explanationBullets.map(item => (
                          <ListItem key={item} sx={{ marginTop: '0 !important' }}>
                            {item}
                          </ListItem>
                        ))}
                      </List>
                    )}
                    <Flex direction='column'>
                      <Link
                        href={coolbluesChoiceLink}
                        onClick={() => handleSeeAllChoicesClick()}
                        startIcon={<ArrowRight />}
                        mt={200}
                      >
                        {t('seeAllChoices', {
                          productType: pluralName,
                          interpolation: { escapeValue: false }
                        })}
                      </Link>

                      {coolbluesChoiceArticleLink && (
                        <Link
                          href={coolbluesChoiceArticleLink}
                          target='_blank'
                          startIcon={<ArrowRight />}
                          mt={200}
                        >
                          {t('learnMore')}
                        </Link>
                      )}
                    </Flex>
                  </GridItem>
                  <GridItem
                    column={specialist ? 'span 8' : 'span 12'}
                    pl={modalMobilePadding}
                    pb={modalMobilePadding}
                    pr={specialist ? 0 : 500}
                    row={2}
                  >
                    <Flex gap={400} alignItems={showOnMobile ? 'normal' : 'center'} mt={400}>
                      <CbcLabelContainer label={label} flexGrow={showOnMobile ? 1 : 0} />
                      <Typography>
                        <Trans
                          t={t}
                          i18nKey='ourChoicePromise'
                          components={{
                            strong: <strong></strong>
                          }}
                        />
                      </Typography>
                    </Flex>
                  </GridItem>
                  {!!specialist && (
                    <GridItem
                      column='span 4'
                      row={showOnMobile ? 'auto / auto' : 'span 2'}
                      pr={modalMobilePadding}
                    >
                      <Flex
                        alignItems='end'
                        justifyContent='end'
                        sx={{
                          width: '100%',
                          height: '100%'
                        }}
                      >
                        <Image
                          imageId={specialist.image.id}
                          fileCollection='content'
                          width={150}
                          height={showOnMobile ? 120 : 200}
                          alt={t('ourChoice', { title })}
                          loading='eager'
                          background='transparent'
                          objectFit='contain'
                          objectPosition='bottom'
                          fullWidth
                        />
                      </Flex>
                    </GridItem>
                  )}
                </Grid>
              </ModalBody>
            }
          />
        )}
      </Flex>
    </div>
  );
}
